/**
 * Web Tooltip Component
 *
 */

import React, { useContext, useEffect, useState } from 'react'
import clsx from 'clsx'
import Context from '../../shared/Context'
import type { ContextProps } from '../../shared/Context'
import { validateDOMAttributes } from '../../shared/component-helper'
import useId from '../../shared/helpers/useId'
import { createSpacingClasses } from '../space/SpacingHelper'
import TooltipWithEvents from './TooltipWithEvents'
import {
  defaultProps,
  getPropsFromTooltipProp,
  getTargetElement,
  injectTooltipSemantic,
} from './TooltipHelpers'
import { TooltipAllProps } from './types'
import { TooltipContext } from './TooltipContext'
import getRefElement from '../../shared/internal/getRefElement'

function Tooltip(localProps: TooltipAllProps) {
  const context = useContext(Context)
  const props = resolveProps(localProps, context)
  const {
    targetElement,
    targetSelector,
    className,
    id,
    size,
    children,
    tooltip,
    fixedPosition,
    skipPortal,
    noAnimation,
    showDelay,
    hideDelay,
    open,
    placement,
    arrow,
    align,
    portalRootClass,
    omitDescribedBy,
    contentRef,
    triggerOffset,
    forceOpen,
    ...attributeProps
  } = props

  const targetSource = targetElement || targetSelector
  const target = useTooltipTarget(targetElement, targetSelector)
  const internalId = useId(id)
  const [isControlled] = useState(() => typeof open === 'boolean')

  if (targetSource && !target) {
    return null
  }

  const classes = buildClassNames(size, className, props)
  const attributes = createAttributes(classes, attributeProps)

  // also used for code markup simulation
  validateDOMAttributes(localProps, attributes)

  return (
    <TooltipContext value={{ isControlled, internalId, props }}>
      <TooltipWithEvents
        target={target}
        attributes={attributes}
        forceOpen={forceOpen}
        {...props}
      >
        {children}
      </TooltipWithEvents>
    </TooltipContext>
  )
}

function resolveProps(
  localProps: TooltipAllProps,
  context: ContextProps
): TooltipAllProps {
  const inherited = getPropsFromTooltipProp(localProps)
  const translation = (context.getTranslation?.(
    localProps as Record<string, unknown>
  ) || {}) as Record<string, unknown>
  const tooltipTranslation = (translation['Tooltip'] || {}) as Record<
    string,
    unknown
  >

  return {
    ...defaultProps,
    ...localProps,
    ...inherited,
    ...tooltipTranslation,
    ...context.Tooltip,
  }
}

function useTooltipTarget(
  targetElement: TooltipAllProps['targetElement'],
  targetSelector: TooltipAllProps['targetSelector']
) {
  const [target, setTarget] = useState<
    TooltipAllProps['targetElement'] | HTMLElement | null
  >(null)
  const source = targetElement || targetSelector

  useEffect(() => {
    if (!source) {
      setTarget(null)
      return
    }

    const resolved =
      getTargetElement(
        typeof source === 'string'
          ? source
          : getRefElement(source as React.RefObject<unknown>)
      ) || (typeof source === 'string' ? null : source)

    setTarget(resolved as HTMLElement | React.ReactElement | null)
  }, [source])

  return target
}

function buildClassNames(
  size: TooltipAllProps['size'],
  className: TooltipAllProps['className'],
  props: TooltipAllProps
) {
  return clsx(
    'dnb-tooltip',
    size === 'large' && 'dnb-tooltip--large',
    createSpacingClasses(props),
    className
  )
}

function createAttributes(
  className: string,
  attributeProps: Record<string, unknown>
): React.HTMLAttributes<HTMLElement> {
  return {
    className,
    ...attributeProps,
  }
}

Tooltip.isTooltipComponent = true

export { injectTooltipSemantic }
export default Tooltip
